import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import Mascot from "@/components/Mascot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, setAuthToken } from "@/lib/http";
import { authErrorKey } from "@/lib/authErrors";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const INVITE_ERR_MAP = {
  INVITE_REQUIRED: "login.invite.required",
  INVITE_INVALID: "login.invite.invalid",
  INVITE_USED: "login.invite.used",
  INVITE_REVOKED: "login.invite.revoked",
  INVITE_EMAIL_MISMATCH: "login.invite.emailMismatch",
};

export default function RegisterPage() {
  const { t, lang } = useT();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [betaInvite, setBetaInvite] = useState(() => searchParams.get("invite") || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error(t("login.passwordTooShort"));
    if (password !== confirmPassword) return toast.error(t("login.passwordsDontMatch"));
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name, lang,
        beta_invite: betaInvite.trim().toUpperCase() || undefined,
      });
      if (data.token) setAuthToken(data.token);
      setUser(data.user);
      navigate("/onboarding", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (!err.response) { toast.error(t("common.networkError")); return; }
      const key = (typeof d === "string" && INVITE_ERR_MAP[d]) ? INVITE_ERR_MAP[d] : authErrorKey(d);
      toast.error(key ? t(key) : (typeof d === "string" ? d : `${t("common.error")} (${err.response.status})`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#FAFAFA]">
      <div className="lg:col-span-3 relative overflow-hidden hidden lg:block">
        <img src="https://images.pexels.com/photos/15319047/pexels-photo-15319047.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2C5254]/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Logo />
          <div>
            <p className="font-editorial text-4xl lg:text-5xl leading-tight max-w-md whitespace-pre-line">
              {t("login.tagline").replace(",", ",\n")}
            </p>
            <p className="mt-6 max-w-md text-white/85 text-base leading-relaxed">{t("login.taglineDesc")}</p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">{t("login.discipline")}</div>
        </div>
      </div>

      <div className="lg:col-span-2 flex flex-col justify-center px-8 py-12 lg:px-16 relative" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 3rem)" }}>
        <div className="absolute top-6 right-6"><LanguageSwitcher /></div>

        <div className="lg:hidden mb-6"><Logo /></div>
        <div className="max-w-md w-full">
          <div className="flex items-center gap-3 mb-3">
            <Mascot size={64} pose="static" alt="Solano" className="shrink-0" />
            <p className="text-xs uppercase tracking-[0.25em] text-[#E8951C] font-semibold">{t("login.portalTag")}</p>
          </div>
          <h1 className="mt-3 font-editorial text-4xl text-[#1A1D1E]">{t("login.registerTitle")}</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">{t("login.registerSubtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="reg-name">{t("login.nameLabel")}</Label>
              <Input id="reg-name" data-testid="register-name" required minLength={2} value={name}
                onChange={(e) => setName(e.target.value)} className="h-11 mt-1" />
            </div>
            <div>
              <Label htmlFor="reg-email">{t("login.emailLabel")}</Label>
              <Input id="reg-email" data-testid="register-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="h-11 mt-1" placeholder="tu@esempio.com" />
            </div>
            <div>
              <Label htmlFor="reg-password">{t("login.passwordLabel")}</Label>
              <Input id="reg-password" data-testid="register-password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} className="h-11 mt-1" placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="reg-cpassword">{t("login.confirmPasswordLabel")}</Label>
              <Input id="reg-cpassword" data-testid="register-confirm" type="password" required value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} className="h-11 mt-1" placeholder="••••••••" />
            </div>
            <Button type="submit" data-testid="register-submit-btn" disabled={loading}
              className="w-full h-11 rounded-full bg-[#E8951C] hover:bg-[#c47a10] text-white gap-2 focus-teal">
              <UserPlus className="h-4 w-4" /> {loading ? "…" : t("login.registerBtn")}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            {t("login.haveAccount")}{" "}
            <button type="button" onClick={() => navigate("/login")} data-testid="go-login-link"
              className="text-[#5A8B8F] font-semibold hover:underline">
              {t("login.loginLink")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
